/**
 * fileTreeUtils — pure helpers for the Explorer file tree (FilesPanel).
 *
 * Kept in their own module (not FilesPanel.tsx) so the component file exports
 * ONLY components — otherwise react-refresh/only-export-components warns and
 * the strict `--max-warnings 0` lint gate fails.
 */
import {
  File,
  FileCode,
  FileJson,
  FileText,
  FileImage,
  type LucideIcon,
} from 'lucide-react';
import type { FileNode } from '@/types';

const FILE_ICONS: Record<string, LucideIcon> = {
  ts: FileCode, tsx: FileCode, js: FileCode, jsx: FileCode,
  css: FileCode, scss: FileCode, html: FileCode, py: FileCode,
  json: FileJson,
  md: FileText, txt: FileText, yaml: FileText, yml: FileText,
  png: FileImage, jpg: FileImage, jpeg: FileImage, gif: FileImage, svg: FileImage, webp: FileImage,
};

/** Map a filename to a per-extension lucide icon (fallback: generic File). */
export function iconForFile(filename: string): LucideIcon {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return FILE_ICONS[ext] ?? File;
}

/** Recursively sort a tree: directories first, then files, each alphabetical
 *  (case-insensitive). Returns a NEW tree; leaves files untouched. */
export function sortFileTree(node: FileNode): FileNode {
  if (node.type !== 'directory' || !node.children) return node;
  const children = node.children
    .map(sortFileTree)
    .sort((a, b) => {
      if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  return { ...node, children };
}

/** Prune a tree to nodes matching `query` (case-insensitive) or on the path to
 *  a match. Empty/whitespace query returns the node unchanged. Returns null
 *  when nothing under `node` matches. */
export function filterFileTree(node: FileNode, query: string): FileNode | null {
  const q = query.trim().toLowerCase();
  if (!q) return node;
  const nameMatches = node.name.toLowerCase().includes(q);
  if (node.type !== 'directory') {
    return nameMatches ? node : null;
  }
  const children = (node.children ?? [])
    .map((c) => filterFileTree(c, query))
    .filter((c): c is FileNode => c !== null);
  if (nameMatches || children.length > 0) {
    return { ...node, children };
  }
  return null;
}

/** Parent directory of a project-relative path. '/a.md' → '', '/src/x.ts' → '/src'. */
export function parentDirOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i <= 0 ? '' : path.slice(0, i);
}
